import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { getBacktestById, BacktestDataType, SavedBacktest, getBacktestAnalysis, saveBacktestAnalysis } from '@/services/backtestStorageService';
import { useToast } from '@/components/ui/use-toast';
import { Download, Share, Calendar, Clock, Info, AlertTriangle, RefreshCw, FileSpreadsheet, FileText } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Skeleton } from '@/components/ui/skeleton';

interface BacktestDetailsViewProps {
  backtestId: string;
  onAnalyze?: (results: any) => void;
}

export const BacktestDetailsView: React.FC<BacktestDetailsViewProps> = ({ backtestId, onAnalyze }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [backtest, setBacktest] = useState<SavedBacktest | null>(null);
  const [backtestData, setBacktestData] = useState<BacktestDataType>({});
  const [analysis, setAnalysis] = useState<{ type: string; content: string }[]>([]);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  // Load backtest data on component mount
  useEffect(() => {
    loadBacktestData();
  }, [backtestId]);

  // Load backtest data and analysis
  const loadBacktestData = async () => {
    setLoading(true);
    
    try {
      console.log('🔍 DEBUG: Loading backtest data for ID:', backtestId);
      const { backtest: backtestDetails, data } = await getBacktestById(backtestId);
      
      console.log('🔍 DEBUG: Received backtest details:', backtestDetails);
      console.log('🔍 DEBUG: Received backtest data:', data);
      console.log('🔍 DEBUG: Trades in data:', data.trades?.length || 0);
      console.log('🔍 DEBUG: Equity curve in data:', data.equityCurve?.length || 0);
      console.log('🔍 DEBUG: Open positions in data:', data.openPositions?.length || 0);
      
      if (!backtestDetails) {
        console.error('❌ No backtest details returned for ID:', backtestId);
        toast({
          title: "Error",
          description: "Failed to load backtest details.",
          variant: "destructive",
        });
        return;
      }
      
      setBacktest(backtestDetails);
      setBacktestData(data);
      
      // Load any existing analysis
      const analysisData = await getBacktestAnalysis(backtestId);
      setAnalysis(analysisData);
      
    } catch (error) {
      console.error("❌ Error loading backtest details:", error);
      toast({
        title: "Error",
        description: "Failed to load backtest details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate analysis for the backtest
  const generateAnalysis = async () => {
    if (!backtest) return;
    
    setAnalyzeLoading(true);
    
    try {
      // For now we'll use a simple template for analysis
      // In a real implementation, this would call an LLM API
      const totalReturn = backtest.performance?.totalReturn || 0;
      const winRate = backtest.performance?.winRate || 0;
      const sharpeRatio = backtest.performance?.sharpeRatio || 0;
      
      let sentiment = "neutral";
      if (totalReturn > 10 && sharpeRatio > 1) {
        sentiment = "very positive";
      } else if (totalReturn > 0) {
        sentiment = "positive";
      } else if (totalReturn < -10) {
        sentiment = "very negative";
      } else if (totalReturn < 0) {
        sentiment = "negative";
      }
      
      const analysisContent = `
# Backtest Analysis

## Performance Summary
The backtest showed a ${sentiment} result with a total return of ${totalReturn.toFixed(2)}%.
The strategy achieved a win rate of ${winRate.toFixed(2)}% across ${backtest.performance?.trades || 0} trades.

## Risk Assessment
The strategy demonstrated a Sharpe ratio of ${sharpeRatio.toFixed(2)}, which indicates 
${sharpeRatio > 1 ? 'good' : sharpeRatio > 0.5 ? 'acceptable' : 'poor'} risk-adjusted returns.
Maximum drawdown was ${backtest.performance?.maxDrawdown?.toFixed(2) || 0}%, which is 
${(backtest.performance?.maxDrawdown || 0) < 10 ? 'relatively contained' : 'significant and requires attention'}.

## Recommendations
- ${totalReturn > 0 ? 'Consider running additional tests with different market conditions to ensure robustness' : 'Review entry and exit conditions to improve performance'}
- ${sharpeRatio > 1 ? 'The risk-adjusted returns look promising' : 'Focus on improving risk management to enhance risk-adjusted returns'}
- ${winRate > 60 ? 'The high win rate suggests the strategy is effective at identifying favorable trading opportunities' : 'Work on improving the win rate through better entry/exit timing'}
`;

      // Save the analysis to the database
      const saveResult = await saveBacktestAnalysis(
        backtestId,
        'overall_performance',
        analysisContent
      );

      if (saveResult) {
        setAnalysis(prev => [{
          type: 'overall_performance',
          content: analysisContent
        }, ...prev]);
        
        toast({
          title: "Analysis Generated",
          description: "The backtest analysis has been generated successfully.",
        });
        
        if (onAnalyze) {
          onAnalyze({
            id: backtestId,
            type: 'overall_performance',
            content: analysisContent
          });
        }
      } else {
        throw new Error("Failed to save analysis");
      }
    } catch (error) {
      console.error("Error generating analysis:", error);
      toast({
        title: "Error",
        description: "Failed to generate backtest analysis.",
        variant: "destructive",
      });
    } finally {
      setAnalyzeLoading(false);
    }
  };

  // Export backtest results
  const exportResults = (format: 'json' | 'csv') => {
    if (!backtest || !backtestData) return;
    
    try {
      if (format === 'json') {
        const jsonContent = JSON.stringify({
          backtest,
          data: backtestData
        }, null, 2);
        
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `backtest_${backtestId}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // CSV export for equity curve
        const csvHeader = 'date,value\n';
        const csvContent = csvHeader + (backtestData.equityCurve || [])
          .map(point => `${point.date},${point.value}`)
          .join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `backtest_equity_curve_${backtestId}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      
      toast({
        title: `Exported as ${format.toUpperCase()}`,
        description: `Backtest data has been exported in ${format.toUpperCase()} format.`,
      });
    } catch (error) {
      console.error(`Error exporting as ${format}:`, error);
      toast({
        title: "Export Failed",
        description: `Failed to export backtest data as ${format.toUpperCase()}.`,
        variant: "destructive",
      });
    }
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to convert day indices to actual dates
  const convertDayIndexToDate = (dayIndex: string | number, startDate: string): string => {
    if ((dayIndex === null || dayIndex === undefined || dayIndex === '') || !startDate) return 'N/A';
    
    // Convert dayIndex to number
    const dayNum = typeof dayIndex === 'string' ? parseInt(dayIndex, 10) : dayIndex;
    
    // If it's not a valid day index, try to parse as regular date
    if (isNaN(dayNum)) {
      return formatDate(dayIndex.toString());
    }
    
    // Convert day index to actual date
    const baseDate = new Date(startDate);
    const actualDate = new Date(baseDate);
    actualDate.setDate(baseDate.getDate() + dayNum);
    
    const result = actualDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Debug logging
    console.log('🔍 DEBUG convertDayIndexToDate:', {
      dayIndex,
      startDate,
      dayNum,
      baseDate: baseDate.toISOString().split('T')[0],
      actualDate: actualDate.toISOString().split('T')[0],
      result
    });
    
    return result;
  };

  // Helper function to format trade dates correctly
  const formatTradeDate = (dateValue: string | number): string => {
    if (!dateValue && dateValue !== 0) return 'N/A';
    
    // Try multiple sources for start date
    const startDate = backtest?.settings?.startDate || 
                     backtest?.startDate || 
                     (backtest as any)?.metadata?.start_date ||
                     (backtestData as any)?.metadata?.start_date;
    
    // Debug logging
    console.log('🔍 DEBUG formatTradeDate:', {
      dateValue,
      startDate,
      backtestSettings: backtest?.settings,
      backtestStartDate: backtest?.startDate,
      backtestMetadata: (backtest as any)?.metadata
    });
    
    if (!startDate) {
      console.warn('No start date found for date conversion, trying fallback');
      // Try to get it from the trades data itself by finding the earliest trade
      if (backtestData.trades && backtestData.trades.length > 0) {
        // Look for any trade that has a proper date format
        for (const trade of backtestData.trades) {
          if (trade.entryDate && trade.entryDate !== "0" && trade.entryDate !== "N/A") {
            // Use the first real date we find and work backwards
            const tradeDate = new Date(trade.entryDate);
            if (!isNaN(tradeDate.getTime())) {
              // If this is a valid date, use it as reference
              return formatDate(trade.entryDate);
            }
          }
        }
      }
      
      // Final fallback - use a default date if we can't find anything
      const fallbackDate = '2025-05-01'; // From the original request
      console.warn('Using fallback date:', fallbackDate);
      return convertDayIndexToDate(dateValue, fallbackDate);
    }
    
    // Check if it's a day index (number as string like "0", "45")
    const dayIndex = typeof dateValue === 'string' ? parseInt(dateValue, 10) : dateValue;
    if (!isNaN(dayIndex)) {
      return convertDayIndexToDate(dayIndex, startDate);
    }
    
    // Otherwise, treat as regular date
    return formatDate(dateValue.toString());
  };

  // Calculate monthly returns from equity curve
  const calculateMonthlyReturns = (equityCurve: Array<{ date: string; value: number }> = []) => {
    if (!equityCurve || equityCurve.length === 0) return [];
    
    const monthlyData: { [key: string]: { first: number; last: number; month: string } } = {};
    
    equityCurve.forEach(point => {
      const date = new Date(point.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          first: point.value,
          last: point.value,
          month: `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`
        };
      } else {
        monthlyData[monthKey].last = point.value;
      }
    });
    
    return Object.entries(monthlyData).map(([key, data]) => ({
      month: data.month,
      return: ((data.last - data.first) / data.first) * 100
    }));
  };

  // Calculate drawdown series from equity curve
  const calculateDrawdownSeries = (equityCurve: Array<{ date: string; value: number }> = []) => {
    if (!equityCurve || equityCurve.length === 0) return [];
    
    let peak = equityCurve[0]?.value || 0;
    const drawdownSeries = [];
    
    for (const point of equityCurve) {
      if (point.value > peak) {
        peak = point.value;
      }
      
      const drawdownPercent = peak === 0 ? 0 : ((peak - point.value) / peak) * 100;
      
      drawdownSeries.push({
        date: point.date,
        drawdown: drawdownPercent
      });
    }
    
    return drawdownSeries;
  };

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
        </div>
        
        <Tabs defaultValue="overview">
          <TabsList className="grid grid-cols-5 mb-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </TabsList>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {Array(8).fill(0).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-16 mb-2" />
                  <Skeleton className="h-6 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Skeleton className="h-80 w-full" />
        </Tabs>
      </div>
    );
  }

  // Error state
  if (!backtest) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load backtest</h3>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">
          The backtest details could not be loaded. It may have been deleted or you may not have permission to view it.
        </p>
        <Button onClick={loadBacktestData}>Try Again</Button>
      </div>
    );
  }

  // Main render
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-2xl font-bold">{backtest.name}</h2>
          {backtest.description && (
            <p className="text-gray-500 mt-1">{backtest.description}</p>
          )}
          
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(backtest.createdAt)}
            </div>
            
            <Badge 
              variant="outline" 
              className={`
                capitalize
                ${backtest.status === 'completed' ? 'bg-green-100 text-green-800 border-green-200' : 
                  backtest.status === 'running' ? 'bg-blue-100 text-blue-800 border-blue-200' : 
                  backtest.status === 'paused' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 
                  'bg-red-100 text-red-800 border-red-200'}
              `}
            >
              {backtest.status}
            </Badge>
            
            <Badge variant="outline" className="capitalize">
              {backtest.type}
            </Badge>
            
            {backtest.strategyName && (
              <Badge variant="secondary">
                Strategy: {backtest.strategyName}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => exportResults('csv')}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportResults('json')}>
            <FileText className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={generateAnalysis} 
            disabled={analyzeLoading}
          >
            {analyzeLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Info className="h-4 w-4 mr-2" />
                Analyze
              </>
            )}
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="equity">Equity Curve</TabsTrigger>
          <TabsTrigger value="trades">Trades</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Return</p>
                <p className={`text-2xl font-bold ${
                  (backtest.performance?.totalReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(backtest.performance?.totalReturn || 0) >= 0 ? '+' : ''}
                  {(backtest.performance?.totalReturn || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Annual Return</p>
                <p className={`text-2xl font-bold ${
                  (backtest.performance?.annualizedReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(backtest.performance?.annualizedReturn || 0) >= 0 ? '+' : ''}
                  {(backtest.performance?.annualizedReturn || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Sharpe Ratio</p>
                <p className="text-2xl font-bold">
                  {(backtest.performance?.sharpeRatio || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Max Drawdown</p>
                <p className="text-2xl font-bold text-red-600">
                  {(backtest.performance?.maxDrawdown || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Win Rate</p>
                <p className="text-2xl font-bold">
                  {(backtest.performance?.winRate || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Profit Factor</p>
                <p className="text-2xl font-bold">
                  {(backtest.performance?.profitFactor || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Trades</p>
                <p className="text-2xl font-bold">
                  {backtest.performance?.trades || 0}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="text-2xl font-bold">
                  {backtest.endDate ? 
                    Math.ceil((new Date(backtest.endDate).getTime() - new Date(backtest.startDate).getTime()) / (1000 * 60 * 60 * 24)) + ' days' 
                    : 'Running'}
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Equity Curve</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={backtestData.equityCurve || []}
                      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(date) => new Date(date).toLocaleDateString()} 
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `$${formatNumber(value)}`}
                      />
                      <Tooltip 
                        formatter={(value: any) => [`$${formatNumber(value)}`, 'Portfolio Value']}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        name="Portfolio Value" 
                        stroke="#2563eb" 
                        dot={false}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Monthly Returns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={calculateMonthlyReturns(backtestData.equityCurve)}
                      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis 
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        formatter={(value: any) => [`${value !== undefined && !isNaN(value) ? value.toFixed(2) : 'N/A'}%`, 'Return']}
                      />
                      <Legend />
                      <ReferenceLine y={0} stroke="#000" />
                      <Bar 
                        dataKey="return" 
                        name="Monthly Return" 
                        fill="#10b981"
                        shape={(props: any) => {
                          const { x, width } = props;
                          let { y, height } = props;
                          const value = props.payload.return;
                          const color = value !== undefined ? (value >= 0 ? '#10b981' : '#ef4444') : '#6b7280';
                          
                          // Fix for negative height values
                          if (height < 0) {
                            // For negative returns, SVG rect needs positive height
                            // Adjust y-coordinate upward and use absolute height value
                            y = y + height; // Move y up by the height amount
                            height = Math.abs(height); // Make height positive
                          }
                          
                          return <rect x={x} y={y} width={width} height={height} fill={color} />;
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Equity Curve Tab */}
        <TabsContent value="equity">
          <Card>
            <CardHeader>
              <CardTitle>Detailed Equity Curve</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={backtestData.equityCurve}
                    margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(date) => new Date(date).toLocaleDateString()} 
                    />
                    <YAxis 
                      domain={['auto', 'auto']}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${formatNumber(value)}`}
                    />
                    <Tooltip 
                      formatter={(value: any) => [`$${formatNumber(value)}`, 'Portfolio Value']}
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      name="Portfolio Value" 
                      stroke="#2563eb" 
                      dot={false}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">Drawdown</h3>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={calculateDrawdownSeries(backtestData.equityCurve)}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(date) => new Date(date).toLocaleDateString()} 
                      />
                      <YAxis 
                        domain={[0, 'dataMax']}
                        reversed={true}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value.toFixed(2)}%`}
                      />
                      <Tooltip 
                        formatter={(value: any) => [`${value.toFixed(2)}%`, 'Drawdown']}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="drawdown" 
                        name="Drawdown" 
                        stroke="#ef4444" 
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Trades Tab */}
        <TabsContent value="trades">
          <Card>
            <CardHeader>
              <CardTitle>Trade Details</CardTitle>
              <CardDescription>
                Complete list of all trades executed during the backtest
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Check if we have trades data from the backtest_data table
                const hasDetailedTrades = backtestData.trades && backtestData.trades.length > 0;
                const hasOpenPositions = backtestData.openPositions && backtestData.openPositions.length > 0;
                const tradesFromPerformance = backtest?.performance?.trades || 0;
                
                console.log('🔍 DEBUG: Trades display logic:', {
                  hasDetailedTrades,
                  detailedTradesCount: backtestData.trades?.length || 0,
                  hasOpenPositions,
                  openPositionsCount: backtestData.openPositions?.length || 0,
                  tradesFromPerformance
                });

                // If no detailed trades but performance shows trades, display a helpful message
                if (!hasDetailedTrades && !hasOpenPositions && tradesFromPerformance > 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12">
                      <AlertTriangle className="w-8 h-8 text-amber-500 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Trade details unavailable</h3>
                      <p className="text-sm text-gray-500 mb-4 max-w-md text-center">
                        This backtest executed {tradesFromPerformance} trades, but detailed trade information could not be loaded. 
                        This may occur with backtests run using fallback data or when there are database connectivity issues.
                      </p>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 max-w-md">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">What you can do:</h4>
                        <ul className="text-xs text-blue-700 space-y-1">
                          <li>• Check the Overview tab for performance metrics</li>
                          <li>• View the Equity Curve tab for portfolio growth</li>
                          <li>• Try running a new backtest for detailed trade data</li>
                        </ul>
                      </div>
                    </div>
                  );
                }

                // If absolutely no trade data
                if (!hasDetailedTrades && !hasOpenPositions && tradesFromPerformance === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12">
                      <AlertTriangle className="w-8 h-8 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No trades executed</h3>
                      <p className="text-sm text-gray-500 mb-4 max-w-md text-center">
                        No trades were executed during this backtest period. This could be due to:
                      </p>
                      <div className="bg-gray-50 p-4 rounded-lg border max-w-md">
                        <ul className="text-xs text-gray-600 space-y-1">
                          <li>• Strategy conditions were not met</li>
                          <li>• Market data was insufficient</li>
                          <li>• Backtest period was too short</li>
                          <li>• Strategy parameters need adjustment</li>
                        </ul>
                      </div>
                    </div>
                  );
                }

                // Display trades and positions if available
                return (
                  <div className="space-y-8">
                    {/* Open Positions Section */}
                    {backtestData.openPositions && backtestData.openPositions.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium mb-4">Open Positions (End of Backtest)</h3>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Symbol</TableHead>
                                <TableHead>Direction</TableHead>
                                <TableHead>Entry Date</TableHead>
                                <TableHead>Entry Price</TableHead>
                                <TableHead>Current Price</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>Unrealized P&L ($)</TableHead>
                                <TableHead>Unrealized P&L (%)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {backtestData.openPositions.map((position: any, index: number) => (
                                <TableRow key={`open-${position.symbol}-${index}`}>
                                  <TableCell className="font-medium">{position.symbol}</TableCell>
                                  <TableCell>
                                    <Badge variant={position.direction === 'buy' || position.direction === 'long' ? 'default' : 'destructive'}>
                                      {position.direction}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{formatTradeDate(position.entryDate)}</TableCell>
                                  <TableCell>${position.entryPrice?.toFixed(2)}</TableCell>
                                  <TableCell>${position.currentPrice?.toFixed(2)}</TableCell>
                                  <TableCell>{position.quantity}</TableCell>
                                  <TableCell className={`${position.unrealizedProfit > 0 ? 'text-green-600' : position.unrealizedProfit < 0 ? 'text-red-600' : ''}`}>
                                    {position.unrealizedProfit > 0 ? '+' : ''}${position.unrealizedProfit?.toFixed(2)}
                                  </TableCell>
                                  <TableCell className={`${position.unrealizedProfitPercent > 0 ? 'text-green-600' : position.unrealizedProfitPercent < 0 ? 'text-red-600' : ''}`}>
                                    {position.unrealizedProfitPercent > 0 ? '+' : ''}
                                    {position.unrealizedProfitPercent?.toFixed(2)}%
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    
                    {/* Closed Trades Section */}
                    {backtestData.trades && backtestData.trades.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium mb-4">Closed Trades</h3>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Symbol</TableHead>
                                <TableHead>Direction</TableHead>
                                <TableHead>Entry Date</TableHead>
                                <TableHead>Exit Date</TableHead>
                                <TableHead>Entry Price</TableHead>
                                <TableHead>Exit Price</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>P&L ($)</TableHead>
                                <TableHead>P&L (%)</TableHead>
                                <TableHead>Reason</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {backtestData.trades.map((trade: any, index: number) => (
                                <TableRow key={trade.id || index}>
                                  <TableCell className="font-medium">{trade.symbol}</TableCell>
                                  <TableCell>
                                    <Badge variant={trade.direction === 'buy' || trade.direction === 'long' ? 'default' : 'destructive'}>
                                      {trade.direction}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{formatTradeDate(trade.entryDate)}</TableCell>
                                  <TableCell>{formatTradeDate(trade.exitDate)}</TableCell>
                                  <TableCell>${trade.entryPrice?.toFixed(2)}</TableCell>
                                  <TableCell>${trade.exitPrice?.toFixed(2)}</TableCell>
                                  <TableCell>{trade.quantity}</TableCell>
                                  <TableCell className={`${trade.profit > 0 ? 'text-green-600' : trade.profit < 0 ? 'text-red-600' : ''}`}>
                                    {trade.profit > 0 ? '+' : ''}${trade.profit?.toFixed(2)}
                                  </TableCell>
                                  <TableCell className={`${trade.profitPercent > 0 ? 'text-green-600' : trade.profitPercent < 0 ? 'text-red-600' : ''}`}>
                                    {trade.profitPercent > 0 ? '+' : ''}
                                    {trade.profitPercent?.toFixed(2)}%
                                  </TableCell>
                                  <TableCell>{trade.reason || 'Signal'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Backtest Configuration</CardTitle>
              <CardDescription>
                Parameters and settings used for this backtest
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-3">Time Period</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Start Date:</span>
                      <span className="font-medium">{formatDate(backtest.settings?.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">End Date:</span>
                      <span className="font-medium">{formatDate(backtest.settings?.endDate)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Timeframe:</span>
                      <span className="font-medium capitalize">{backtest.settings?.timeframe || 'day'}</span>
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <h3 className="text-lg font-medium mb-3">Capital</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Initial Capital:</span>
                      <span className="font-medium">${parseInt(backtest.settings?.initialCapital || '0').toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <h3 className="text-lg font-medium mb-3">Trading Symbols</h3>
                  <div className="space-y-2">
                    {(() => {
                      // Try multiple sources for symbols
                      let symbols: string[] = [];
                      
                      // First try metadata from backtest or backtestData
                      if ((backtest as any)?.metadata?.symbols) {
                        symbols = (backtest as any).metadata.symbols;
                      } else if ((backtestData as any)?.metadata?.symbols) {
                        symbols = (backtestData as any).metadata.symbols;
                      }
                      // Then try settings (type cast to include symbols)
                      else if ((backtest?.settings as any)?.symbols) {
                        symbols = Array.isArray((backtest.settings as any).symbols) 
                          ? (backtest.settings as any).symbols.map((s: any) => typeof s === 'string' ? s : s.ticker)
                          : [];
                      }
                      // Finally try extracting from trades data
                      else if (backtestData.trades && backtestData.trades.length > 0) {
                        const uniqueSymbols = [...new Set(backtestData.trades.map((trade: any) => trade.symbol))];
                        symbols = uniqueSymbols;
                      }
                      
                      return symbols.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {symbols.map((symbol: string, index: number) => (
                            <Badge key={index} variant="outline" className="font-mono">
                              {symbol}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No symbols specified</span>
                      );
                    })()}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-3">Market Simulation</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Data Provider:</span>
                      <span className="font-medium capitalize">{backtest.settings?.provider || 'unknown'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Include Commission:</span>
                      <Badge variant={backtest.settings?.includeCommission ? "default" : "outline"}>
                        {backtest.settings?.includeCommission ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    {backtest.settings?.includeCommission && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Commission Rate:</span>
                        <span className="font-medium">{backtest.settings?.commissionRate || '0.00'}%</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Include Slippage:</span>
                      <Badge variant={backtest.settings?.includeSlippage ? "default" : "outline"}>
                        {backtest.settings?.includeSlippage ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    {backtest.settings?.includeSlippage && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Slippage Model:</span>
                        <span className="font-medium capitalize">{backtest.settings?.slippageModel || 'fixed'}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Market Environment:</span>
                      <span className="font-medium capitalize">{backtest.settings?.marketEnvironment || 'normal'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Analysis Tab */}
        <TabsContent value="analysis">
          <Card>
            <CardHeader>
              <CardTitle>Strategy Analysis</CardTitle>
              <CardDescription>
                AI-generated insights and recommendations based on backtest results
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analysis.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Info className="w-12 h-12 text-blue-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No analysis available</h3>
                  <p className="text-sm text-gray-500 mb-4 max-w-md text-center">
                    Generate an analysis of this backtest to get AI-powered insights and recommendations.
                  </p>
                  <Button 
                    onClick={generateAnalysis} 
                    disabled={analyzeLoading}
                  >
                    {analyzeLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Info className="h-4 w-4 mr-2" />
                        Generate Analysis
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {analysis.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <h3 className="text-lg font-medium capitalize">
                        {item.type.replace(/_/g, ' ')} Analysis
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-md whitespace-pre-line">
                        {item.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};